// src/pages/EmbedDashboard.js
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslations } from '../translations/translations';
import { fetchResources } from '../redux/dataSlice';
import ResourceCard from '../components/Resources/ResourceCard';
import ResourceFilters from '../components/Resources/ResourceFilters';
import Pagination from '../components/Pagination';
import { Spinner } from "@/components/ui/spinner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Search, Filter, Users } from 'lucide-react';

const EmbedDashboard = () => {
  const dispatch = useDispatch();
  const { currentLanguage } = useLanguage();
  const t = useTranslations();
  
  const { items: resources, status, error } = useSelector((state) => state.resources[currentLanguage]);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    tipo: [],
    tema: [],
    organizacion: [],
    pais: []
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9);

  useEffect(() => {
    if (status === 'idle') {
      dispatch(fetchResources(currentLanguage));
    }
  }, [status, dispatch, currentLanguage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);

  useEffect(() => {
    setSearchTerm("");
    setFilters({ tipo: [], tema: [], organizacion: [], pais: [] });
    setCurrentPage(1);
  }, [currentLanguage]);

  const filteredResources = resources.filter(resource => {
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTipo = filters.tipo.length === 0 || 
                       (Array.isArray(resource.tipo) && 
                        resource.tipo.some(tipo => filters.tipo.includes(tipo)));
    const matchesTema = filters.tema.length === 0 || 
                       (Array.isArray(resource.tema) && 
                        resource.tema.some(tema => filters.tema.includes(tema)));
    const matchesOrganizacion = filters.organizacion.length === 0 || 
                               filters.organizacion.includes(resource.organizacion);
    const matchesPais = filters.pais.length === 0 || 
                       filters.pais.includes(resource.pais);
    return matchesSearch && matchesTipo && matchesTema && matchesOrganizacion && matchesPais;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredResources.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredResources.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleSearchChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    setCurrentPage(1);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleFilterClick = (filterType, value) => {
    setFilters(prevFilters => {
      const currentValues = prevFilters[filterType];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      return { ...prevFilters, [filterType]: newValues };
    });
  };

  if (status === 'loading') {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
        <span className="ml-2">{t.general.loading}</span>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <Alert variant="destructive">
        <AlertDescription>{t.general.error} {error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="bg-white py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" id="recursos">
        <ResourceFilters
          searchTerm={searchTerm}
          setSearchTerm={handleSearchChange}
          filters={filters}
          setFilters={handleFilterChange}
          resourcesData={resources}
          icons={{
            search: <Search className="h-5 w-5 text-gray-400" />,
            tipo: <Filter className="h-5 w-5 text-gray-400" />,
            tema: <Filter className="h-5 w-5 text-gray-400" />,
            organizacion: <Users className="h-5 w-5 text-gray-400" />
          }}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          {currentItems.map((resource) => (
            <ResourceCard 
              key={resource.id} 
              resource={resource} 
              onFilterClick={handleFilterClick}
            />
          ))}
        </div>

        {filteredResources.length > itemsPerPage && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            paginate={paginate}
          />
        )}

        {filteredResources.length === 0 && (
          <p className="text-center text-gray-500 mt-8">{t.general.noResults}</p>
        )}
      </div>
    </div>
  );
};

export default EmbedDashboard;